import CryptoJS from "crypto-js";
import {
  ISingleBinaryKeys,
  generateSingleBinary,
} from "./GenerateSingleBinary";
import encryptFile from "./encryptFile";
import { readFileAsBlob } from "./readFileAsBlob";
import { createChunks } from "./createChunksFromBlob";
import axios from "axios";
import * as openpgp from "openpgp";

export const axiosInstance = axios.create({
  baseURL:
    import.meta.env.VITE_APP_API_URL ?? "https://beta.pocztaprawnicza.pl",
  headers: {
    "content-type": "application/json",
  },
  withCredentials: true,
});

const encryptFiles = async (files: File[] | Blob[], keys: any) => {
  const blobs = await Promise.all(
    files.map(async (file) => {
      const fileAndBlob = await readFileAsBlob(file);
      const returnedFile = fileAndBlob[0];
      const returnedBlob = fileAndBlob[1];

      const { message } = await openpgp.encrypt({
        //@ts-ignore
        message: openpgp.message.fromBinary(
          new Uint8Array(returnedBlob.target.result as ArrayBuffer)
        ),
        publicKeys: [keys.keys[0]],
        armor: false,
      });

      const secured = await new Uint8Array(message.packets.write());

      return {
        orginalFile: returnedFile,
        encryptedFile: secured,
        blob: returnedBlob,
        hash: JSON.stringify(
          await CryptoJS.SHA256(
            (returnedBlob.target?.result as string) ?? ""
          ).toString(CryptoJS.enc.Hex)
        ),
      };
    })
  );

  return blobs;
};

const Files = async (files: File[] | Blob[]) => {
  const blobs = await Promise.all(
    files.map(async (file) => {
      const fileAndBlob = await readFileAsBlob(file);
      const returnedFile = fileAndBlob[0];
      const returnedBlob = fileAndBlob[1];

      return {
        orginalFile: returnedFile,
        blob: returnedBlob,
        hash: JSON.stringify(
          await CryptoJS.SHA256(
            (returnedBlob.target?.result as string) ?? ""
          ).toString(CryptoJS.enc.Hex)
        ),
      };
    })
  );

  return blobs;
};

export const handleCreate = async (
  files: File[] | Blob[],
  publicKey: any,
  useEncrypt: boolean,
  useDecrypt: boolean,
  // token przesyłki (Sid z /init) — wysyłany jako nagłówek X-Contact-Sid
  token: string,
  fileName?: string,
  fileType?: string,
  callback?: (index: number, progress: number) => void
) => {
  if (!files.length) {
    return;
  }

  if (useEncrypt) {
    const encryptedFiles = await encryptFiles(files, publicKey);

    const uris = await initUpload(
      encryptedFiles,
      useDecrypt,
      token,
      fileName,
      fileType,
      callback
    );
    return uris;
  }
  const encryptedFiles = await Files(files);
  const uris = await initUpload(
    encryptedFiles,
    useDecrypt,
    token,
    fileName,
    fileType,
    callback
  );
  return uris;
};

// Initialize upload sending information about files
const initUpload = async (
  encryptedFiles: any[],
  useDecrypt: boolean,
  token: string,
  fileName?: string,
  fileType?: string,
  callback?: (index: number, progress: number) => void
) => {
  return await Promise.all(
    encryptedFiles.map(async (file: any, index) => {
      const response = await axiosInstance.post(
        "/contactform/upload",
        {
          ContentType: fileType ? fileType : file.orginalFile.type,
          FileName: fileName ? fileName : file.orginalFile.name,
          Hash: file.hash.replaceAll('"', ""),
          IsSigned: false,
          Size: file.orginalFile.size,
        },
        { headers: { "X-Contact-Sid": token } }
      );
      await getLink(response.data, file, token, (progress) =>
        callback?.(index, progress)
      );
      return { uri: response.data.Uri, encrypted: useDecrypt };
    })
  );
};

// Getting link to S3
const getLink = async (
  uploadResponse: any,
  file: any,
  token: string,
  callback?: (progress: number) => void
) => {
  const chunks = createChunks(
    new Blob([file.encryptedFile ?? file.orginalFile])
  );

  const links = [];

  for (const chunk in chunks) {
    const response = await axiosInstance.get(
      `/contactform/part?uri=${uploadResponse.Uri}&partNumber=${
        Number(chunk) + 1
      }&uploadId=${uploadResponse.UploadId}`,
      { headers: { "X-Contact-Sid": token } }
    );
    links.push(response.data);
  }
  await uploadFile(
    uploadResponse.Uri,
    uploadResponse.UploadId,
    links,
    file,
    token,
    callback
  );
};

const uploadFile = async (
  uri: string,
  uploadId: string,
  links: string[],
  file: any,
  token: string,
  callback?: (progress: number) => void
) => {
  const chunks = createChunks(
    new Blob([file.encryptedFile ?? file.orginalFile])
  );
  const etags: string[] = [];

  let sendedChunk = 0;
  for (const chunk of chunks) {
    const etag = await sendChunk(
      links[sendedChunk],
      chunk,
      sendedChunk + 1,
      chunks.length,
      callback
    );

    etags.push(etag as string);
    sendedChunk++;
  }

  axiosInstance.post(
    "/contactform/confirm",
    {
      UploadId: uploadId,
      Uri: uri,
      Parts: etags.map((etag, index) => ({ Etag: etag, PartNumber: index + 1 })),
      // Parts: [{ ETag: etag!, PartNumber: 1 }],
    },
    { headers: { "X-Contact-Sid": token } }
  );
};

const sendChunk = async (
  link: string,
  chunk: Blob,
  actualChunk: number,
  maxChunk: number,
  callback?: (progress: number) => void
) => {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", link, true);
    xhr.responseType = "blob";

    // Oczekujemy, że AWS S3 może zwrócić 200 OK, 201 Created lub 204 No Content w odpowiedzi na zapytanie PUT
    xhr.onload = function () {
      if (xhr.status === 200 || xhr.status === 201 || xhr.status === 204) {
        var etag = this.getResponseHeader("ETag");
        // etags.push(etag!);
        resolve(etag?.replaceAll('"', ""));
      } else {
        console.error(
          "Wystąpił błąd podczas wysyłania pliku do AWS S3. Status:",
          xhr.status
        );
      }
    };

    xhr.upload.onprogress = (e) => {
      //
      // const filePercent = (e.loaded / e.total) * 100
      const filePercent =
        ((actualChunk - 1 + e.loaded / e.total) / maxChunk) * 100;
      callback?.(filePercent);
    };

    xhr.send(chunk);
  });
};
